/* eslint no-constant-condition: 0 */
/* eslint no-unused-vars: 0 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash.debounce';
import clsx from 'clsx';
import svgFetcher from 'acq-components/lib/utils/svgFetcher';
import {
  sendDTMClickEvent,
  sendDTMCustomEvent
} from 'service_modules/models/dtm-tagging';
import { Element } from '@americanexpress/aqx-components';
import FilterButtons from './FilterButtons';
import { isIOS, isIE11 } from 'core_modules/bootstrap/browsers/browser-detection';
import { getWinWidth } from 'src/universal/utils/clientSideDetection';
import styles from './__styles__/enhancedHorizontalFilter.scss?modules';

const ArrowCta = ({ ctaObj = {}, clickHandler, arrowType = '' }) => {
  const ariaVal = `Scroll ${arrowType}`;
  const onClickHandler = () => {
    sendDTMClickEvent(ctaObj?.dtm);
    clickHandler();
  };
  return (
    <button
      className={styles[ctaObj.name]}
      onClick={onClickHandler}
      aria-label={ariaVal}
      title={ariaVal}
    >
      {svgFetcher(ctaObj.imageName, 'dls') || null}
    </button>
  );
};

/*
US3711477:
(1) this component builds on top of an existing aqx-component by adding arrow ctas on both sides
(2) deliberately not using redux.connect for easiler future relocation/repackaging as needed.
*/
const EnhancedFilter = ({ filterData, activeFilter }) => {
  const needArrowRecalcOnLoad = useRef(false);
  const deliberateArrowClsNameChange = useRef(false);
  const [showArrowCls, setShowArrowCls] = useState(styles.hideBothArrows);
  const eventTarget = useRef(null);
  const needToDoImpression = useRef(false);
  const impressedList = useRef([]);
  const containerVal = 'div[class*="purify_index__container-"]';
  const containerSelector = `.${styles.filterStrip} ${containerVal}`;
  const { sortedList = [], buttons = [] } = filterData;
  const currWinWidth = getWinWidth();
  const isMobile = currWinWidth <= 767;

  const calcArrowVisibilities = useCallback(
    () => {
      const itemContainerEle = eventTarget.current;
      let resultClassName = styles.hideBothArrows;

      if (itemContainerEle) {
        const maxScrollLeftVal =
          itemContainerEle.scrollWidth - itemContainerEle.clientWidth;
        const currScrollLeft = itemContainerEle.scrollLeft;
        const scrollLeftAdjusted =
          currScrollLeft > 0 ? currScrollLeft + 15 : currScrollLeft;

        if (maxScrollLeftVal >= 0) {
          if (scrollLeftAdjusted >= maxScrollLeftVal) {
            resultClassName = styles.showLeftArrowOnly;
          } else if (scrollLeftAdjusted <= 0) {
            resultClassName = styles.showRightArrowOnly;
          } else {
            resultClassName = styles.showBothArrows;
          }
        }
      }

      deliberateArrowClsNameChange.current = true;
      setShowArrowCls(resultClassName);
    },
    [eventTarget.current, needToDoImpression.current]
  );

  const getShowArrowClsNameVal = useCallback(
    () => {
      /*
    US5116590: We need to do a proximate calc so to reduce impact
    on CLS score on initial loading.
    */
      needArrowRecalcOnLoad.current = false;

      if (deliberateArrowClsNameChange.current) {
        setTimeout(() => {
          deliberateArrowClsNameChange.current = false;
        }, 50);
        return showArrowCls;
      }

      if (isMobile) {
        return styles.hideBothArrows;
      }

      let resultVal = styles.showBothArrows;
      let availableWidth = currWinWidth - 90 * 2;
      if (availableWidth > 900) {
        availableWidth = 900;
      }
      const maxCntCanFit = Math.round(availableWidth / 140);
      const listArr = sortedList || [];
      const activeIndex = listArr.findIndex(
        item => item.internalId === activeFilter
      );
      const totalCnt = listArr.length;

      if (activeIndex < maxCntCanFit) {
        resultVal = styles.showRightArrowOnly;
      } else if (activeIndex >= totalCnt - 1) {
        resultVal = styles.showLeftArrowOnly;
      } else {
        needArrowRecalcOnLoad.current = true;
      }

      return resultVal;
    },
    [
      activeFilter,
      showArrowCls,
      isMobile,
      currWinWidth,
      deliberateArrowClsNameChange.current
    ]
  );

  const createOnArrowClick = useCallback(
    isLeft => {
      return () => {
        const itemContainerEle = eventTarget.current;

        if (!itemContainerEle) {
          return;
        }

        const currPosX = itemContainerEle.scrollLeft;
        const incrementX = itemContainerEle.clientWidth;
        const nextScrollX = isLeft ? currPosX - incrementX : currPosX + incrementX;

        if (typeof itemContainerEle.scrollTo === 'function') {
          itemContainerEle.scrollTo({
            top: itemContainerEle.scrollTop,
            left: nextScrollX,
            behavior: 'smooth'
          });
        } else {
          itemContainerEle.scrollLeft = nextScrollX;
        }

        calcArrowVisibilities();
      };
    },
    [eventTarget.current]
  );

  const debouncedHandler = debounce(calcArrowVisibilities, 500);

  useEffect(() => {
    // US3711477: we need to dynamically show or hide arrow CTAs
    const timeout1 = setTimeout(() => {
      const itemContainerEle = document.querySelector(containerSelector);

      if (itemContainerEle) {
        eventTarget.current = itemContainerEle;
        itemContainerEle.addEventListener('scroll', debouncedHandler);
      }
    }, isIOS() || isIE11 ? 1500 : 200);

    const timeout2 = setTimeout(() => {
      if (needArrowRecalcOnLoad.current) {
        calcArrowVisibilities();
      }
    }, isIOS() || isIE11 ? 2000 : 500);

    return () => {
      if (eventTarget.current) {
        eventTarget.current.removeEventListener('scroll', debouncedHandler);
        eventTarget.current = null;
      }
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, []);

  useEffect(
    () => {
      /*
    DE500800: covering 2 edge cases: when partiallly displayed item is clicked, we need
    to bring the item into the visible area with mininum visual disturnbance.  This can
    happen from either right or left side.
    */
      const timeout = setTimeout(() => {
        const activeIndexVal = (sortedList || []).findIndex(
          item => item.internalId === activeFilter
        );
        if (activeIndexVal > -1) {
          const offsetWidth = 40;
          const itemContainerEle = document.querySelector(containerSelector);
          const activeEle = document.querySelector(
            `${containerSelector} > div:nth-of-type(${activeIndexVal + 1})`
          );
          if (itemContainerEle && activeEle) {
            const eleRectObj = activeEle.getBoundingClientRect();
            const containerRect = itemContainerEle.getBoundingClientRect();
            const currEleWidth = eleRectObj.width;
            const visibleLeft = eleRectObj.left - containerRect.left;
            const requiredLeft = containerRect.width - currEleWidth;
            if (visibleLeft - requiredLeft > 20) {
              const distanceVal = visibleLeft - requiredLeft + offsetWidth;
              itemContainerEle.scrollLeft =
                itemContainerEle.scrollLeft + distanceVal;
            } else if (visibleLeft < -20) {
              itemContainerEle.scrollLeft =
                itemContainerEle.scrollLeft - currEleWidth;
            }
          }
        }
      }, isIOS() || isIE11 ? 500 : 20);

      return () => clearTimeout(timeout);
    },
    [activeFilter]
  );

  return (
    <Element className={clsx(styles.filterStrip, getShowArrowClsNameVal())}>
      <ArrowCta
        ctaObj={buttons[0]}
        clickHandler={createOnArrowClick(true)}
        arrowType="left"
      />
      <FilterButtons
        filterData={filterData}
        activeFilter={activeFilter}
        onFilterClick={() => {
          setTimeout(() => {
            calcArrowVisibilities();
          }, 200);
        }}
      />
      <ArrowCta
        ctaObj={buttons[1]}
        clickHandler={createOnArrowClick(false)}
        arrowType="right"
      />
    </Element>
  );
};

export default React.memo(EnhancedFilter);
